/**
 * Protected official titles & names — must NEVER be translated.
 *
 * Policy: Translate the UI, preserve the identity. Songs, albums, singles,
 * band members, characters and branded terms stay verbatim regardless of
 * the user's locale.
 *
 * Sorted longest-first so the mask helper replaces the most specific terms
 * before any sub-strings.
 */

const RAW_TERMS = [
  // ===== Band, brand, domain =====
  "Eiffel 65", "E65.io", "E65",
  // ===== Band members & people =====
  "Jeffrey Jey", "Maurizio Lobina", "Gabry Ponte",
  "Bliss Corporation", "Franco Battiato", "Loredana Bertè", "Guè", "Shiva",
  // ===== Characters =====
  "Zorotl", "Bloom 06",
  // ===== Albums =====
  "Europop", "Contact!",
  // Self-titled & Episodes
  "Eiffel 65 (Italian Version)", "Eiffel 65 (English Version)",
  "Episode I", "Episode II",
  // ===== Singles & songs =====
  "Blue (Da Ba Dee)", "Da Ba Dee",
  "Move Your Body",
  "Too Much of Heaven", "Too Much Of Heaven",
  "My Console", "One Goal", "Back In Time", "Back in Time",
  "Lucky (In My Life)", "Lucky in My Life", "Lucky",
  "80's Stars", "80s Stars",
  "Losing You", "Cosa Resterà",
  "Quelli Che Non Hanno Età",
  "Viaggia Insieme A Me",
  "Una Notte E Forse Mai Più",
  "Tu Credi",
  "Voglia Di Dance All Night",
  "Panico", "Critical", "Auto Blu", "Heaven", "Hyperlink",
  "Bestiale", "Fare A Meno Di Te",
  "Elephants In Amsterdam", "Elephants in Amsterdam",
  "Blue 25", "Blue (Da Ba Dee) 25",
  // ===== Tour / era names =====
  "Reunion", "Europop Era", "Bloom 06 Era", "Bliss Corporation Era",
  // ===== Game / app titles (official) =====
  "Blue World", "Blue World Adventure",
  "Jeffrey's Penalty Challenge",
  "E65 Concert Manager",
  "Fan Quiz", "Eiffel 65 Fan Quiz",
  // ===== Labels =====
  "Bliss Co.",
];

// Dedupe + sort longest-first
export const PROTECTED_TERMS = [...new Set(RAW_TERMS)]
  .filter((s) => s && s.length > 1)
  .sort((a, b) => b.length - a.length);

// Use private-use unicode markers that LLMs almost never alter.
// «E65T0» — guillemets + literal sentinel; cheap to detect & restore.
const SENTINEL_OPEN = "\u00abE65T";
const SENTINEL_CLOSE = "\u00bb";

/** Escapes a string for use inside a RegExp. */
function escapeRe(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Replace every occurrence of a PROTECTED_TERM in `text` with a sentinel.
 * Returns { masked, terms } where `terms[i]` is the original literal.
 *
 * Matching is case-insensitive (German/French/Italian users may type lower-case)
 * but the ORIGINAL casing from `text` is preserved on restore.
 */
export function maskProtectedTerms(text) {
  if (!text || typeof text !== "string") return { masked: text, terms: [] };
  const terms = [];
  let masked = text;

  for (const term of PROTECTED_TERMS) {
    // Word-ish boundary: avoid matching inside a longer word
    // (e.g. don't match "lucky" inside "luckily").
    const re = new RegExp(`(?<![A-Za-z0-9])${escapeRe(term)}(?![A-Za-z0-9])`, "gi");
    masked = masked.replace(re, (matched) => {
      const idx = terms.push(matched) - 1;
      return `${SENTINEL_OPEN}${idx}${SENTINEL_CLOSE}`;
    });
  }
  return { masked, terms };
}

/**
 * Restore sentinels in the translated text back to original literals.
 * Tolerant against LLM-introduced spaces or quote-marks around the sentinel.
 */
export function restoreProtectedTerms(translated, terms) {
  if (!translated || !terms || terms.length === 0) return translated;
  let out = translated;
  // Match the sentinel even if the LLM added stray spaces or quoted it.
  const re = new RegExp(`["'\\s]*${escapeRe(SENTINEL_OPEN)}\\s*(\\d+)\\s*${escapeRe(SENTINEL_CLOSE)}["'\\s]*`, "g");
  out = out.replace(re, (_full, idxStr) => {
    const i = parseInt(idxStr, 10);
    return Number.isFinite(i) && terms[i] !== undefined ? terms[i] : _full;
  });
  return out;
}

/** Returns true if `text` contains any protected term — useful for quick gating. */
export function hasProtectedTerm(text) {
  if (!text || typeof text !== "string") return false;
  return PROTECTED_TERMS.some((t) =>
    new RegExp(`(?<![A-Za-z0-9])${escapeRe(t)}(?![A-Za-z0-9])`, "i").test(text)
  );
}
